/**
 * React Query hooks for diary entries.
 * React Query hooks for the corresponding entity.
 */

import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { useMemo } from "react";

// Re-export types
export type DiaryEntry = {
  guid: string;
  diaryEntryText: string;
  farmerImageUploadId: string;
  fieldGuid: string;
  entryDate: string;
  creationDate: string;
};

export type CreateDiaryEntryPayload = {
  diaryEntryText: string;
  farmerImageUploadId: string;
  fieldGuid: string;
  entryDate: string;
};

export const DIARY_ENTRY_REJECTION_PERIOD_DAYS = 30;

/**
 * Extended API class for diary entries
 */
class DiaryEntryAPI extends API<DiaryEntry> {
  async getAllDiaryEntriesForFarm(farmGuid: string): Promise<DiaryEntry[]> {
    const res = await fetch(
      `${this.baseUrl}${this.entity}?farmGuid=${farmGuid}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    return (await res.json()) as DiaryEntry[];
  }

  async createDiaryEntry(
    payload: CreateDiaryEntryPayload
  ): Promise<{ guid: string }> {
    const res = await fetch(`${this.baseUrl}${this.entity}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    return (await res.json()) as { guid: string };
  }
}

const diaryEntryApi = new DiaryEntryAPI("diaryEntry");

/**
 * Fetch all diary entries for a farm
 */
export function useDiaryEntriesForFarm(farmId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.events.diary.byFarm(farmId ?? ""),
    queryFn: async () => {
      if (!farmId) {
        return [];
      }
      const entries = await diaryEntryApi.getAllDiaryEntriesForFarm(farmId);
      // Sort by entry date descending
      return entries.sort((a, b) => {
        return Date.parse(b.entryDate) - Date.parse(a.entryDate);
      });
    },
    enabled: !!farmId,
  });
}

/**
 * Fetch a single diary entry by ID
 */
export function useDiaryEntry(diaryEntryId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.events.diary.detail(diaryEntryId ?? ""),
    queryFn: async () => {
      if (!diaryEntryId) {
        throw new Error("Diary entry ID is required");
      }
      return await diaryEntryApi.getOne(diaryEntryId);
    },
    enabled: !!diaryEntryId,
  });
}

/**
 * Get diary entries for a specific field
 */
export function useDiaryEntriesForField(
  farmId: string | undefined,
  fieldGuid: string | undefined
) {
  const { data: allEntries, ...rest } = useDiaryEntriesForFarm(farmId);

  const filteredEntries = useMemo(() => {
    if (!fieldGuid || !allEntries) return [];
    return allEntries
      .filter((entry) => entry.fieldGuid === fieldGuid)
      .sort((a, b) => Date.parse(b.entryDate) - Date.parse(a.entryDate));
  }, [allEntries, fieldGuid]);

  return {
    data: filteredEntries,
    ...rest,
  };
}

/**
 * Create a new diary entry
 */
export function useCreateDiaryEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: CreateDiaryEntryPayload & { farmId: string }
    ) => {
      const { farmId, ...apiPayload } = payload;
      const response = await diaryEntryApi.createDiaryEntry(apiPayload);
      return response.guid;
    },
    onSuccess: async (_, variables) => {
      // Invalidate the diary entries list for this farm
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.diary.byFarm(variables.farmId),
      });
    },
  });
}

/**
 * Delete a diary entry
 */
export function useDeleteDiaryEntry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { diaryEntry: DiaryEntry; farmId: string }) => {
      const res = await diaryEntryApi.deleteOne(params.diaryEntry.guid);
      if (!res.ok) {
        throw new Error(`Failed to delete diary entry: ${res.status}`);
      }
      return params;
    },
    onSuccess: async ({ farmId }) => {
      // Invalidate the diary entries list for this farm
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.diary.byFarm(farmId),
      });
    },
  });
}

/**
 * Prefetch diary entries for a farm (used during initialization)
 */
export async function fetchDiaryEntriesForFarmQuery(
  farmId: string,
  queryClient: QueryClient
): Promise<DiaryEntry[]> {
  const entries = await diaryEntryApi.getAllDiaryEntriesForFarm(farmId);
  const sorted = entries.sort((a, b) => {
    return Date.parse(b.entryDate) - Date.parse(a.entryDate);
  });
  queryClient.setQueryData(queryKeys.events.diary.byFarm(farmId), sorted);
  return sorted;
}

/**
 * Reset diary entries query cache
 */
export function resetDiaryEntriesQueryCache(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: queryKeys.events.diary.all });
}
